import {size, transparentize} from 'polished'
import animation from './animation'
import colors from './colors'

import type {StyleProps} from '@miro-site/core'
import {outlines} from './focus'

const sizes = {
  small: {
    borderRadius: 40,
    fontSize: 14,
    lineHeight: 20,
  },
  medium: {
    borderRadius: 24,
    fontSize: 18,
    lineHeight: 24,
  },
  large: {
    borderRadius: 40,
    fontSize: 24,
    lineHeight: 32,
  },
}

type Sizes = typeof size

const spacing: Record<keyof Sizes, StyleProps> = {
  small: {
    paddingLeft: 19,
    paddingRight: 19,
    paddingTop: 8,
    paddingBottom: 10,
  },
  medium: {
    paddingLeft: 23,
    paddingRight: 23,
    paddingTop: 9,
    paddingBottom: 13,
  },
  large: {
    paddingLeft: 31,
    paddingRight: 31,
    paddingTop: 13,
    paddingBottom: 17,
  },
}

const withArrowSpacing: Record<keyof Sizes, StyleProps> = {
  small: {
    paddingLeft: 19,
    paddingRight: 15,
    paddingTop: 8,
    paddingBottom: 10,
  },
  medium: {
    paddingLeft: 23,
    paddingRight: 19,
    paddingTop: 9,
    paddingBottom: 13,
  },
  large: {
    paddingLeft: 31,
    paddingRight: 21,
    paddingTop: 13,
    paddingBottom: 17,
  },
}

const buttons = {
  variants: {
    white: {
      background: colors.primaryWhite,
      color: colors.primaryIndigo,
      border: `1px solid ${colors.primaryWhite}`,
      ':hover': {
        background: 'rgba(255, 255, 255, 0.9)',
        borderColor: 'transparent',
      },
      ':active': {
        background: 'rgba(255, 255, 255, 0.9)',
        borderColor: 'transparent',
      },
      ':focus': {
        ...outlines.default,
      },
    },
    transparent: {
      background: colors.primaryWhite,
      color: colors.primaryBlue,
      border: `1px solid ${colors.primaryBlue}`,
      ':hover': {
        background: 'rgba(69, 91, 237, 0.06)',
        borderColor: colors.primaryBlueHover,
      },
      ':active': {
        background: 'rgba(69, 91, 237, 0.06)',
        borderColor: colors.primaryBlueHover,
      },
      ':focus': {
        ...outlines.default,
      },
    },
    transparentWhite: {
      background: 'transparent',
      color: colors.primaryWhite,
      border: `1px solid ${colors.primaryWhite}`,
      ':hover': {
        background: 'transparent',
        borderColor: 'rgba(255, 255, 255, 0.7)',
      },
      ':active': {
        background: 'transparent',
        borderColor: 'rgba(255, 255, 255, 0.7)',
      },
      ':focus': {
        ...outlines.default,
      },
    },
    primary: {
      background: colors.primaryBlue,
      color: colors.primaryWhite,
      border: `1px solid ${colors.primaryBlue}`,
      ':hover': {
        background: colors.primaryBlueHover,
        borderColor: colors.primaryBlueHover,
      },
      ':active': {
        background: colors.primaryBlueActive,
        borderColor: colors.primaryBlueActive,
      },
      ':focus': {
        ...outlines.default,
      },
    },
    secondary: {
      background: colors.primaryWhite,
      color: colors.primaryIndigo,
      border: '1px solid #CDCCD7',
    },
    secondaryBlue: {
      background: colors.primaryWhite,
      border: `1px solid ${colors.primaryBlue}`,
      color: colors.primaryBlue,
      '&:hover': {
        background: transparentize(0.94, colors.primaryBlue),
      },
      '&:active': {
        background: transparentize(0.94, colors.primaryBlue),
      },
      '&:focus': {
        ...outlines.default,
      },
    },
    dark: {
      background: 'transparent',
      color: colors.primaryWhite,
      border: '1px solid rgba(255, 255, 255, 0.4)',
      transition: 'border-color ' + animation.transitionDuration,
      '&:hover': {
        background: 'transparent',
        borderColor: 'rgba(255, 255, 255, 0.1)',
      },
      '&:active': {
        background: 'transparent',
        borderColor: 'rgba(255, 255, 255, 0.1)',
      },
      '&:focus': {
        ...outlines.default,
      },
    },
    dropdown: {
      textAlign: 'left',
      color: colors.primaryIndigo,
      background: colors.primaryWhite,
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: transparentize(0.8, colors.primaryIndigo),
      borderRadius: 40,
      '&& > span': {
        paddingRight: 48,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
      '&:hover': {
        borderColor: transparentize(0.6, colors.primaryIndigo),
      },
      '&:active, &[aria-expanded="true"]': {
        borderColor: colors.primaryIndigo,
      },
    },
  },
  sizes,
  spacing,
  withArrowSpacing,
}

export default buttons
